import React, { useEffect, useState } from "react";
import { Share, Plus, X, Smartphone } from "lucide-react";
import { useI18n } from "../lib/i18n";

const STORAGE_KEY = "lh2o_pwa_prompt_v1";
const DELAY_MS = 1200;

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function detectIOS() {
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
}
function detectAndroid() {
  return /Android/i.test(navigator.userAgent || "");
}

/**
 * Smart "Add to Home Screen" install prompt.
 *
 * Behavior:
 *  - On Android Chrome/Edge: listens for `beforeinstallprompt`, shows our own
 *    bottom-sheet with an "Install" CTA that fires the native dialog.
 *  - On iOS Safari: shows step-by-step instructions (Share → Add to Home Screen).
 *  - Already installed: renders nothing.
 *  - User dismissed once: stays dismissed for 30 days.
 *
 * Intended to be placed on the BookingConfirmation page so it appears at the
 * highest-converting moment.
 */
export default function InstallPwaPrompt() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [platform, setPlatform] = useState("other"); // ios | android | other

  useEffect(() => {
    if (isStandalone()) return undefined;
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) {
      const ts = parseInt(dismissed, 10);
      if (!Number.isNaN(ts) && Date.now() - ts < 30 * 24 * 60 * 60 * 1000) {
        return undefined;
      }
    }

    const ios = detectIOS();
    const android = detectAndroid();
    setPlatform(ios ? "ios" : android ? "android" : "other");

    let androidTimer;
    const handler = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
      androidTimer = setTimeout(() => setVisible(true), DELAY_MS);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // For iOS we don't get an event — show on a small delay anyway
    let iosTimer;
    if (ios) {
      iosTimer = setTimeout(() => setVisible(true), DELAY_MS);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      if (iosTimer) clearTimeout(iosTimer);
      if (androidTimer) clearTimeout(androidTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setVisible(false);
  };

  const installAndroid = async () => {
    if (!deferredEvent) return;
    try {
      await deferredEvent.prompt();
      const choice = await deferredEvent.userChoice;
      if (choice && choice.outcome === "accepted") {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
        setVisible(false);
      }
    } catch (_) {
      /* ignore */
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] md:inset-x-auto md:right-6 md:bottom-6 md:max-w-sm z-[110] install-prompt-enter"
      data-testid="install-pwa-prompt"
    >
      <div className="rounded-3xl bg-white border border-slate-200 shadow-2xl shadow-sky-900/20 overflow-hidden">
        <div className="bg-gradient-to-br from-sky-600 to-sky-500 px-5 py-4 flex items-center gap-3 text-white">
          <div className="w-10 h-10 rounded-2xl bg-white/20 grid place-items-center backdrop-blur">
            <Smartphone className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[15px]">{t("pwa.title")}</div>
            <div className="text-[12px] text-sky-100/90">
              {t("pwa.subtitle")}
            </div>
          </div>
          <button
            onClick={dismiss}
            className="text-white/80 hover:text-white p-1"
            data-testid="install-pwa-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {platform === "ios" && (
            <div className="space-y-2.5">
              <Step num={1} text={t("pwa.ios_step1")}>
                <Share className="w-4 h-4" />
              </Step>
              <Step num={2} text={t("pwa.ios_step2")}>
                <Plus className="w-4 h-4" />
              </Step>
              <Step num={3} text={t("pwa.ios_step3")} />
            </div>
          )}

          {platform === "android" && deferredEvent && (
            <button
              onClick={installAndroid}
              className="w-full btn-primary py-3 inline-flex items-center justify-center gap-2"
              data-testid="install-pwa-android-btn"
            >
              <Plus className="w-4 h-4" />
              {t("pwa.install_btn")}
            </button>
          )}

          {platform === "android" && !deferredEvent && (
            <p className="text-sm text-slate-600">{t("pwa.android_manual")}</p>
          )}

          {platform === "other" && (
            <p className="text-sm text-slate-600">{t("pwa.fallback")}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({ num, text, children }) {
  return (
    <div className="flex items-start gap-3 text-sm text-slate-700">
      <div className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 grid place-items-center text-[11px] font-bold shrink-0 mt-0.5">
        {num}
      </div>
      <div className="flex-1 leading-snug">
        {text}
        {children && (
          <span className="inline-flex items-center gap-1 ml-1 text-slate-500 align-middle">
            ({children})
          </span>
        )}
      </div>
    </div>
  );
}
